import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Business,
  People,
  AttachMoney,
  TrendingUp,
  PersonAdd,
  QrCode
} from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import { superAdminService } from '../../../services/superAdmin/superAdminService';

const KpiCard = ({ icon: Icon, title, value, subtitle, color = '#14b8a6' }) => (
  <Paper sx={{
    p: 3,
    borderRadius: 3,
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    height: '100%'
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5, fontSize: '0.8rem' }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${color}15`
      }}>
        <Icon sx={{ color, fontSize: 24 }} />
      </Box>
    </Box>
  </Paper>
);

const formatCurrency = (val) => {
  if (val == null) return 'AED 0';
  return `AED ${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const SuperAdminDashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['superAdminDashboard'],
    queryFn: async () => {
      const result = await superAdminService.getDashboard();
      if (!result.success) throw new Error(result.message);
      return result.data;
    },
    refetchInterval: 60000
  });

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const d = data || {};

  const kpis = [
    { icon: Business, title: 'Total Companies', value: d.totalCompanies || 0, subtitle: `${d.activeCompanies || 0} active, ${d.inactiveCompanies || 0} inactive`, color: '#3b82f6' },
    { icon: People, title: 'Total Users', value: d.totalUsers || 0, color: '#8b5cf6' },
    { icon: AttachMoney, title: 'Total Revenue', value: formatCurrency(d.totalRevenue), color: '#10b981' },
    { icon: TrendingUp, title: 'Revenue This Month', value: formatCurrency(d.revenueThisMonth), subtitle: `Last 30 days: ${formatCurrency(d.revenueLast30Days)}`, color: '#14b8a6' },
    { icon: PersonAdd, title: 'Signups This Month', value: d.signupsThisMonth || 0, color: '#f59e0b' },
    { icon: QrCode, title: 'Payment Codes', value: `${d.activePaymentCodes || 0}`, subtitle: `${d.usedPaymentCodes || 0} used, ${d.expiredPaymentCodes || 0} expired`, color: '#ec4899' }
  ];

  // Prepare chart data
  const monthlyRevenue = d.monthlyRevenue || [];
  const chartData = monthlyRevenue.map(m => m.revenue || 0);
  const chartLabels = monthlyRevenue.map(m => {
    const [year, month] = (m.month || '').split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#0f172a' }}>
        Dashboard
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((kpi, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <KpiCard {...kpi} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Monthly Revenue
            </Typography>
            {chartData.length > 0 ? (
              <BarChart
                xAxis={[{ scaleType: 'band', data: chartLabels }]}
                series={[{ data: chartData, color: '#14b8a6', label: 'Revenue (AED)' }]}
                height={300}
              />
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No revenue data yet</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Signups */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Signups
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(d.recentSignups || []).map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {s.companyName || 'N/A'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {s.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.status}
                          size="small"
                          color={s.status === 'success' ? 'success' : s.status === 'failed' ? 'error' : 'warning'}
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!d.recentSignups || d.recentSignups.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography variant="body2" color="text.secondary">No signups yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminDashboard;
